import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  Category as CategoryIcon,
  Inventory as InventoryIcon,
  AttachMoney as AttachMoneyIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Store as StoreIcon,
  LibraryBooks as LibraryBooksIcon,
  Info as InfoIcon,
  ShoppingCart as ShoppingCartIcon
} from '@mui/icons-material';

const ProductLibrary = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Mock product data with grameenbazar store toggle
  const [products, setProducts] = useState([
    {
      id: 1,
      name: 'তাজা টমেটো',
      category: 'সবজি',
      basePrice: 45,
      unit: 'কেজি',
      status: 'উপলব্ধ',
      rating: 4.5,
      usedByStores: 8,
      grameenbazarEnabled: true,
      grameenbazarPrice: 48
    },
    {
      id: 2,
      name: 'দেশি মুরগি',
      category: 'মাংস',
      basePrice: 280,
      unit: 'কেজি',
      status: 'উপলব্ধ',
      rating: 4.8,
      usedByStores: 5,
      grameenbazarEnabled: false,
      grameenbazarPrice: 300
    },
    {
      id: 3,
      name: 'কমলা লেবু',
      category: 'ফল',
      basePrice: 120,
      unit: 'কেজি',
      status: 'উপলব্ধ',
      rating: 4.2,
      usedByStores: 12,
      grameenbazarEnabled: true,
      grameenbazarPrice: 125
    },
    {
      id: 4,
      name: 'বাসমতি চাল',
      category: 'চাল ও ডাল',
      basePrice: 85,
      unit: 'কেজি',
      status: 'উপলব্ধ',
      rating: 4.6,
      usedByStores: 15,
      grameenbazarEnabled: true,
      grameenbazarPrice: 88
    },
    {
      id: 5,
      name: 'গরুর দুধ',
      category: 'দুগ্ধজাত',
      basePrice: 65,
      unit: 'লিটার',
      status: 'উপলব্ধ',
      rating: 4.7,
      usedByStores: 10,
      grameenbazarEnabled: false,
      grameenbazarPrice: 70
    }
  ]);

  const categories = ['সবজি', 'ফল', 'মাংস', 'মাছ', 'চাল ও ডাল', 'দুগ্ধজাত', 'মিষ্টি', 'মসলা'];

  // Statistics
  const totalProducts = products.length;
  const grameenbazarProducts = products.filter(p => p.grameenbazarEnabled).length;
  const totalStoreUsage = products.reduce((sum, p) => sum + p.usedByStores, 0);

  const handleFilterOpen = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    handleFilterClose();
  };

  const handleActionMenuOpen = (event, product) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedProduct(product);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedProduct(null);
  };

  const handleAddProduct = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
  };

  const handleGrameenbazarToggle = (productId) => {
    setProducts(products.map(product => 
      product.id === productId 
        ? { ...product, grameenbazarEnabled: !product.grameenbazarEnabled }
        : product
    ));
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.category.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = selectedFilter === 'all' || 
                         (selectedFilter === 'grameenbazar' && product.grameenbazarEnabled) ||
                         (selectedFilter === 'available' && product.status === 'উপলব্ধ');
    
    return matchesSearch && matchesFilter;
  });

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}>
            <LibraryBooksIcon sx={{ fontSize: 40 }} />
            প্রডাক্ট লাইব্রেরী
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            কেন্দ্রীয় প্রডাক্ট ভান্ডার - দোকানদাররা এখান থেকে প্রডাক্ট নির্বাচন করতে পারবেন
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddProduct}
          sx={{
            background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
            color: 'white',
            px: 3,
            py: 1.5,
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            '&:hover': {
              background: 'linear-gradient(45deg, #3d1789 30%, #0a5ea8 90%)',
              boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
            }
          }}
        >
          লাইব্রেরীতে প্রডাক্ট যোগ করুন
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon />}>
        <AlertTitle>প্রডাক্ট লাইব্রেরী সম্পর্কে</AlertTitle>
        এই লাইব্রেরীতে যোগ করা প্রডাক্টগুলো সব দোকানদার দেখতে পাবেন এবং তাদের দোকানে যোগ করতে পারবেন। 
        গ্রামীণবাজার নিজস্ব স্টোরেও প্রডাক্ট বিক্রি করতে চাইলে "গ্রামীণবাজার স্টোর" টগল চালু করুন।
      </Alert>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2 }}>
                <LibraryBooksIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" gutterBottom>লাইব্রেরীতে প্রডাক্ট</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{totalProducts}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2 }}>
                <ShoppingCartIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" gutterBottom>গ্রামীণবাজার স্টোরে</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{grameenbazarProducts}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2 }}>
                <StoreIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" gutterBottom>দোকানে ব্যবহৃত</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{totalStoreUsage}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.3)', mr: 2 }}>
                <CategoryIcon />
              </Avatar>
              <Box>
                <Typography variant="body2" gutterBottom>ক্যাটাগরি</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>{categories.length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search and Filter Bar */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            placeholder="প্রডাক্ট বা ক্যাটাগরি অনুসন্ধান করুন..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={handleFilterOpen}
            sx={{ minWidth: 120 }}
          >
            ফিল্টার
          </Button>
        </Box>
        
        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleFilterSelect('all')}>
            সব প্রডাক্ট
          </MenuItem>
          <MenuItem onClick={() => handleFilterSelect('grameenbazar')}>
            গ্রামীণবাজার স্টোরের প্রডাক্ট
          </MenuItem>
          <MenuItem onClick={() => handleFilterSelect('available')}>
            উপলব্ধ প্রডাক্ট
          </MenuItem>
        </Menu>
      </Paper>

      {/* Products Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>প্রডাক্ট</TableCell>
                <TableCell>ক্যাটাগরি</TableCell>
                <TableCell align="right">বেস মূল্য</TableCell>
                <TableCell align="center">দোকানে ব্যবহার</TableCell>
                <TableCell align="center">রেটিং</TableCell>
                <TableCell align="center">গ্রামীণবাজার স্টোর</TableCell>
                <TableCell align="center">অবস্থা</TableCell>
                <TableCell align="center">অ্যাকশন</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow key={product.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar 
                        variant="rounded" 
                        sx={{ width: 48, height: 48, bgcolor: 'primary.light' }}
                      >
                        {product.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          লাইব্রেরী আইডি: #{product.id}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={product.category} 
                      variant="outlined"
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      ৳{product.basePrice}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      প্রতি {product.unit}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <StoreIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {product.usedByStores} দোকান
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                      <StarIcon sx={{ fontSize: 16, color: '#ff9800' }} />
                      <Typography variant="body2">{product.rating}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={product.grameenbazarEnabled ? "গ্রামীণবাজার স্টোরে বিক্রি হচ্ছে" : "গ্রামীণবাজার স্টোরে বিক্রি হচ্ছে না"}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={product.grameenbazarEnabled}
                              onChange={() => handleGrameenbazarToggle(product.id)}
                              size="small"
                              color="primary"
                            />
                          }
                          label=""
                          sx={{ m: 0 }}
                        />
                      </Tooltip>
                      {product.grameenbazarEnabled && (
                        <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                          ৳{product.grameenbazarPrice}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={product.status}
                      color="success"
                      size="small"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => handleActionMenuOpen(e, product)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={handleActionMenuClose}>
          <VisibilityIcon sx={{ mr: 1, fontSize: 18 }} />
          বিস্তারিত দেখুন
        </MenuItem>
        <MenuItem onClick={handleActionMenuClose}>
          <EditIcon sx={{ mr: 1, fontSize: 18 }} />
          সম্পাদনা করুন
        </MenuItem>
        <MenuItem onClick={handleActionMenuClose} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 18 }} />
          লাইব্রেরী থেকে মুছুন
        </MenuItem>
      </Menu>

      {/* Add Product Dialog */}
      <Dialog open={openAddDialog} onClose={handleCloseAddDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          লাইব্রেরীতে নতুন প্রডাক্ট যোগ করুন
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                এই প্রডাক্টটি লাইব্রেরীতে যোগ হবে এবং সকল দোকানদার দেখতে পাবেন
              </Alert>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="প্রডাক্টের নাম"
                variant="outlined"
                placeholder="যেমন: তাজা টমেটো"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>ক্যাটাগরি</InputLabel>
                <Select
                  label="ক্যাটাগরি"
                  defaultValue=""
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="বেস মূল্য (দোকানদারদের জন্য রেফারেন্স)"
                variant="outlined"
                type="number"
                placeholder="০"
                InputProps={{
                  startAdornment: <InputAdornment position="start">৳</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="একক"
                variant="outlined"
                placeholder="কেজি, লিটার, পিস"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch color="primary" />}
                label="গ্রামীণবাজার স্টোরেও এই প্রডাক্ট বিক্রি করুন"
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="গ্রামীণবাজার স্টোর মূল্য"
                variant="outlined"
                type="number"
                placeholder="০"
                helperText="গ্রামীণবাজার স্টোরে বিক্রয় মূল্য"
                InputProps={{
                  startAdornment: <InputAdornment position="start">৳</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="প্রডাক্টের বিবরণ"
                variant="outlined"
                multiline
                rows={3}
                placeholder="প্রডাক্ট সম্পর্কে বিস্তারিত লিখুন..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseAddDialog} variant="outlined">
            বাতিল করুন
          </Button>
          <Button 
            onClick={handleCloseAddDialog} 
            variant="contained"
            sx={{
              background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
            }}
          >
            লাইব্রেরীতে যোগ করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add product to library"
        onClick={handleAddProduct}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)',
          '&:hover': {
            background: 'linear-gradient(45deg, #3d1789 30%, #0a5ea8 90%)',
          }
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default ProductLibrary; 